import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, FormControl, InputLabel, RadioGroup, FormControlLabel, Radio, InputAdornment, IconButton, Select, MenuItem
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import impuestoService, { ImpuestoReadDto, CreateImpuestoDto, UpdateImpuestoDto } from '../../services/impuestoService';
import ImputableSearch from '../../components/ImputableSearch';
import { Imputable } from '../../services/imputableService';

const TIPOS = ['Compra', 'Venta'];

const TIPO_IMPUESTO_SIAP = [
  'RET PER IVA',
  'RET PER IIBB',
  'RET PER GAN',
  'RET PER OTROS',
  'PER IIBB',
  'PER IVA',
  'PER GAN',
  'OTROS'
];

const defaultForm: CreateImpuestoDto = {
  codigoImpuesto: '',
  descripcion: '',
  tipo: 'Compra',
  ctaCtable: undefined,
  basadoen: 1,
  valor: undefined,
  codigoSiap: undefined,
  tipoImpuesto: undefined,
  montoGravMin: undefined,
  montoGravMax: undefined,
  montoImpuestoMin: undefined,
  montoImpuestoMax: undefined,
  idJurisdiccion: undefined,
  idRegimen: undefined
};

const ImpuestoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoImpuesto: codigoParam } = useParams();
  const codigoDecoded = codigoParam ? decodeURIComponent(codigoParam) : '';
  const isEdit = !!(codigoParam && codigoParam !== 'nuevo');

  const [formData, setFormData] = useState<CreateImpuestoDto>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imputableSearchOpen, setImputableSearchOpen] = useState(false);
  const [ctaCtableLabel, setCtaCtableLabel] = useState('');

  const loadImpuesto = useCallback(async () => {
    if (!isEdit || !codigoDecoded) return;
    try {
      setLoading(true);
      const imp = await impuestoService.getImpuesto(codigoDecoded);
      const dto = imp as ImpuestoReadDto & { CodigoImpuesto?: string; Descripcion?: string; Tipo?: string; CtaCtable?: number; Basadoen?: number; Valor?: number; CodigoSiap?: string; TipoImpuesto?: string; MontoGravMin?: number; MontoGravMax?: number; MontoImpuestoMin?: number; MontoImpuestoMax?: number; IdJurisdiccion?: string; IdRegimen?: string };
      setFormData({
        codigoImpuesto: dto.codigoImpuesto || dto.CodigoImpuesto || codigoDecoded,
        descripcion: dto.descripcion || dto.Descripcion || '',
        tipo: dto.tipo || dto.Tipo || 'Compra',
        ctaCtable: dto.ctaCtable ?? dto.CtaCtable ?? undefined,
        basadoen: dto.basadoen ?? dto.Basadoen ?? 1,
        valor: dto.valor ?? dto.Valor ?? undefined,
        codigoSiap: dto.codigoSiap || dto.CodigoSiap || undefined,
        tipoImpuesto: dto.tipoImpuesto || dto.TipoImpuesto || undefined,
        montoGravMin: dto.montoGravMin ?? dto.MontoGravMin ?? undefined,
        montoGravMax: dto.montoGravMax ?? dto.MontoGravMax ?? undefined,
        montoImpuestoMin: dto.montoImpuestoMin ?? dto.MontoImpuestoMin ?? undefined,
        montoImpuestoMax: dto.montoImpuestoMax ?? dto.MontoImpuestoMax ?? undefined,
        idJurisdiccion: dto.idJurisdiccion || dto.IdJurisdiccion || undefined,
        idRegimen: dto.idRegimen || dto.IdRegimen || undefined
      });
      if (dto.ctaCtable ?? dto.CtaCtable) {
        setCtaCtableLabel(`${dto.ctaCtable ?? dto.CtaCtable}`);
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar el impuesto', { variant: 'error' });
      navigate('/configuraciones/impuestos');
    } finally {
      setLoading(false);
    }
  }, [isEdit, codigoDecoded, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadImpuesto();
  }, [isEdit, loadImpuesto]);

  const handleImputableSelect = (imputable: Imputable) => {
    setFormData(prev => ({ ...prev, ctaCtable: imputable.nroCuenta }));
    setCtaCtableLabel(imputable.nomCuenta || imputable.descripcioncta || `${imputable.nroCuenta}`);
    setImputableSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (!formData.descripcion?.trim()) {
        enqueueSnackbar('La descripción es requerida', { variant: 'warning' });
        return;
      }
      if (isEdit) {
        const updateDto: UpdateImpuestoDto = {
          descripcion: formData.descripcion,
          tipo: formData.tipo || null,
          ctaCtable: formData.ctaCtable ?? null,
          basadoen: formData.basadoen ?? null,
          valor: formData.valor ?? null,
          codigoSiap: formData.codigoSiap || null,
          tipoImpuesto: formData.tipoImpuesto || null,
          montoGravMin: formData.montoGravMin ?? null,
          montoGravMax: formData.montoGravMax ?? null,
          montoImpuestoMin: formData.montoImpuestoMin ?? null,
          montoImpuestoMax: formData.montoImpuestoMax ?? null,
          idJurisdiccion: formData.idJurisdiccion || null,
          idRegimen: formData.idRegimen || null
        };
        await impuestoService.updateImpuesto(codigoDecoded, updateDto);
      } else {
        if (!formData.codigoImpuesto?.trim()) {
          enqueueSnackbar('El código del impuesto es requerido', { variant: 'warning' });
          return;
        }
        await impuestoService.createImpuesto(formData);
      }
      enqueueSnackbar('Impuesto guardado exitosamente', { variant: 'success' });
      navigate('/configuraciones/impuestos');
    } catch (error: any) {
      console.error('Error al guardar impuesto:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.errors?.general?.[0] || 'Error al guardar el impuesto';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const num = (v: number | string | null | undefined): number | '' => {
    if (v == null || v === '') return '';
    const n = Number(v);
    return isNaN(n) ? '' : n;
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {isEdit ? 'Modificando un Registro' : 'Nuevo Impuesto'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Código Impuesto"
                  value={formData.codigoImpuesto || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigoImpuesto: e.target.value }))}
                  fullWidth
                  required
                  disabled={isEdit}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Descripción"
                  value={formData.descripcion || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  fullWidth
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Tipo</Typography>
                  <RadioGroup row value={formData.tipo || 'Compra'} onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}>
                    <FormControlLabel value="Compra" control={<Radio />} label="Compra" />
                    <FormControlLabel value="Venta" control={<Radio />} label="Venta" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Nº Cuenta"
                  type="number"
                  value={formData.ctaCtable ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => ({ ...prev, ctaCtable: v === '' ? undefined : Number(v) }));
                    if (!v) setCtaCtableLabel('');
                  }}
                  fullWidth
                  placeholder={ctaCtableLabel || 'Buscar cuenta...'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setImputableSearchOpen(true)} edge="end" size="small">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                {ctaCtableLabel && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {ctaCtableLabel}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Basado En</Typography>
                  <RadioGroup row value={formData.basadoen ?? 1} onChange={(e) => setFormData(prev => ({ ...prev, basadoen: Number(e.target.value) }))}>
                    <FormControlLabel value={1} control={<Radio />} label="Porcentaje de Ventas / Compras" />
                    <FormControlLabel value={2} control={<Radio />} label="Monto Fijo Por Unidad" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Valor"
                  type="number"
                  value={num(formData.valor)}
                  onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ step: 0.01, min: 0 }}
                  placeholder={formData.basadoen === 1 ? '%' : 'Monto'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Montos</Typography>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Gravable Mínimo"
                        type="number"
                        value={num(formData.montoGravMin)}
                        onChange={(e) => setFormData(prev => ({ ...prev, montoGravMin: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        fullWidth
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Impuesto Mínimo"
                        type="number"
                        value={num(formData.montoImpuestoMin)}
                        onChange={(e) => setFormData(prev => ({ ...prev, montoImpuestoMin: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        fullWidth
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Gravable Máximo"
                        type="number"
                        value={num(formData.montoGravMax)}
                        onChange={(e) => setFormData(prev => ({ ...prev, montoGravMax: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        fullWidth
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Impuesto Máximo"
                        type="number"
                        value={num(formData.montoImpuestoMax)}
                        onChange={(e) => setFormData(prev => ({ ...prev, montoImpuestoMax: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        fullWidth
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Código Siap"
                  value={formData.codigoSiap || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigoSiap: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Id Jurisdicción"
                  value={formData.idJurisdiccion || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, idJurisdiccion: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel>Tipo Imp. Siap</InputLabel>
                  <Select
                    value={formData.tipoImpuesto || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipoImpuesto: e.target.value || undefined }))}
                    label="Tipo Imp. Siap"
                  >
                    <MenuItem value="">Ninguno</MenuItem>
                    {TIPO_IMPUESTO_SIAP.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Id Régimen"
                  value={formData.idRegimen || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, idRegimen: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/configuraciones/impuestos')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                ESC-Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ImputableSearch
        open={imputableSearchOpen}
        onClose={() => setImputableSearchOpen(false)}
        onSelect={handleImputableSelect}
        tipoMovimientoIE="E"
      />
    </Box>
  );
};

export default ImpuestoForm;
